import { MouseEvent, useCallback, useMemo } from 'react'

import { PointCloudOctree } from '@datalabs-jp/three-loader'
import { getClickCoords } from '@editor/services/Points'
import { useThree } from '@react-three/fiber'
import { Intersection, Object3D, Object3DEventMap, PerspectiveCamera, Points } from 'three'

import { getObjectAreaId } from '../utils'

export const usePointSelection = (
  pointClouds: (Points | PointCloudOctree | undefined)[],
  camera: PerspectiveCamera,
  getSceneRect?: () => { x: number; y: number; width: number; height: number },
) => {
  const { raycaster } = useThree()
  const pcds = useMemo(() => pointClouds.filter((pcd) => pcd) as (Points | PointCloudOctree)[], [pointClouds])

  const getIntersection = useCallback(
    (ev: MouseEvent<HTMLDivElement>): Intersection<Object3D<Object3DEventMap>> | null => {
      const targetElem = document.getElementById('canvas-container')?.getBoundingClientRect()
      if (!targetElem) {
        return null
      }

      const { x, y, width, height } = targetElem
      const { coords } = getClickCoords(
        {
          x: ev?.clientX || 0,
          y: ev?.clientY || 0,
        },
        getSceneRect ? getSceneRect() : { x, y, width, height },
      )

      raycaster.params.Points.threshold = 0.02
      raycaster.far = camera.far
      raycaster.near = camera.near
      raycaster.setFromCamera(coords, camera)
      const intersects = raycaster.intersectObjects(pcds, true)

      const intersectedPoint = intersects.reduce<Intersection<Object3D<Object3DEventMap>> | null>((prev, current) => {
        const { index, faceIndex, distance } = current
        if (index === undefined && faceIndex === undefined) return prev
        if (!prev || distance < prev.distance) return current
        return prev
      }, null)

      return intersectedPoint
    },
    [camera, pcds, raycaster, getSceneRect],
  )

  const projectPoint = useCallback(
    (ev: MouseEvent<HTMLDivElement>) => {
      const intersectedPoint = getIntersection(ev)
      return intersectedPoint?.point || null
    },
    [getIntersection],
  )

  const getProjectedAreaId = useCallback(
    (ev: MouseEvent<HTMLDivElement>) => {
      const intersectedPoint = getIntersection(ev)
      if (!intersectedPoint) return null
      const { object } = intersectedPoint

      return getObjectAreaId(object)
    },
    [getIntersection],
  )

  return { projectPoint, getProjectedAreaId }
}
